import React, { forwardRef } from 'react';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import {
  ColDef,
  ColGroupDef,
  ExcelStyle,
  IAggFunc,
  IAggFuncParams,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import { AG_GRID_LOCALE_KO } from 'constants/agGridLocaleKO';

interface IProps<TData> {
  AgGridRef?: React.RefObject<AgGridReact<any>>;
  rowData: TData[] | null;
  colDefs: (ColDef<TData> | ColGroupDef<TData>)[] | null;
  defaultColDef: ColDef;
  groupIncludeFooter?: boolean;
  groupIncludeTotalFooter?: boolean;
  headerChildren?: React.ReactNode;
  rightButtonChildren?: React.ReactNode;
  tooltipShowDelay?: number;
  tooltipHideDelay?: number;
  height?: string;
  rowHeight?: number;
  setImageExcelIds?: (imageExcelIds: number[]) => void;
  pinnedBottomRowData?: any[];
  onClickRow?: (e: RowClickedEvent) => void;
}

function AgGrid<TData>(props: IProps<TData>) {
  let Ref = props.AgGridRef;
  if (!props?.AgGridRef) {
    Ref = React.useRef<AgGridReact>(null);
  }

  const localeText = React.useMemo<{ [key: string]: string }>(() => {
    return AG_GRID_LOCALE_KO;
  }, []);

  const aggFuncs = React.useMemo<{ [key: string]: IAggFunc }>(() => {
    return {
      svg: roundedAvgAggFunction,
    };
  }, []);

  // 검색 인풋창 변경 시 필터링 옵션 변경
  const handleFilterBoxChange = React.useCallback(() => {
    Ref?.current!.api.setGridOption(
      'quickFilterText',
      (document.getElementById('filter-text-box') as HTMLInputElement).value,
    );
  }, []);

  // 정렬,필터 초기화 버튼 클릭
  const onClickResetBtn = () => {
    if (Ref?.current) {
      Ref?.current.api.resetQuickFilter();
      Ref?.current.api.resetColumnGroupState();
      Ref?.current.api.resetColumnState();
      Ref?.current!.api.setGridOption('quickFilterText', '');
      const searchInput = document.getElementById('filter-text-box') as HTMLInputElement;
      searchInput.value = '';
    }
  };

  // 엑셀다운로드 버튼 클릭
  const onClickExcelDown = () => {
    if (Ref?.current) Ref?.current.api.exportDataAsExcel();
  };

  const onFilterChanged = () => {
    if (Ref?.current!.api) {
      // 현재 보이는 rowData 가져오기
      // const filteredRows = Ref?.current!.api.getModel().rowsToDisplay;
      const filteredRows = Ref?.current!.api.getSelectedRows();

      const filteredRowData = filteredRows.filter((a: { data: { subEvaluationId: number } }) => {
        return a?.data?.subEvaluationId !== undefined;
      });

      if (props.setImageExcelIds) {
        props.setImageExcelIds(
          filteredRowData.map((asd: { data: { subEvaluationId: number } }) => asd.data.subEvaluationId),
        );
      }
    }
  };

  // footer custom
  const autoGroupColumnDef = React.useMemo<ColDef>(() => {
    return {
      cellRendererParams: {
        innterRenderer: (props: CustomCellRendererProps) => {
          const footer = props.node.footer;
          const isRootLevel = props.node.level === -1;
          const value = props.value;

          if (footer) {
            if (isRootLevel) {
              return <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>Grand Total</span>;
            } else {
              return <span style={{ textDecoration: 'underline' }}>Sub Total {value}</span>;
            }
          } else {
            return <span>{value}</span>;
          }
        },
      },
    };
  }, []);

  const excelStyles = React.useMemo<ExcelStyle[]>(() => {
    return [
      {
        id: 'header',
        font: {
          bold: true,
        },
        alignment: {
          vertical: 'Center',
          horizontal: 'Center',
        },
        interior: {
          color: '#f8f8f8',
          pattern: 'Solid',
          patternColor: undefined,
        },
        borders: {
          borderTop: {
            lineStyle: 'Continuous',
            weight: 1,
          },
          borderLeft: {
            lineStyle: 'Continuous',
            weight: 1,
          },
          borderRight: {
            lineStyle: 'Continuous',
            weight: 1,
          },
          borderBottom: {
            lineStyle: 'Continuous',
            weight: 1,
          },
        },
      },
      {
        id: 'headerGroup',
      },
      {
        id: 'cell',
        alignment: {
          vertical: 'Center',
          horizontal: 'Center',
        },
      },
      // {
      //   id: 'pinnedBottomRow',
      //   font: {
      //     bold: true,
      //   },
      //   alignment: {
      //     vertical: 'Center',
      //     horizontal: 'Center',
      //   },
      //   borders: {
      //     borderTop: {
      //       color: '#075453',
      //       lineStyle: 'Continuous',
      //       weight: 1,
      //     },
      //     borderBottom: {
      //       color: '#075453',
      //       lineStyle: 'Continuous',
      //       weight: 1,
      //     },
      //   },
      // },
    ];
  }, []);

  const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    if (params.node.rowPinned) {
      return { fontWeight: 'bold' };
    }
    return { cursor: 'pointer' };
  };

  React.useEffect(() => {
    if (Ref?.current && Ref.current?.api) {
      const allColumnIds: string[] = [];
      Ref.current?.api.getColumns()?.forEach((column) => {
        allColumnIds.push(column.getId());
      });
      if (allColumnIds.length > 0) {
        Ref.current?.api.autoSizeColumns(allColumnIds, false);
      }
    }
  }, [props.rowData, props.colDefs]);

  return (
    <div
      className={'ag-theme-quartz wrap'}
      style={{
        width: '100%',
        height: props?.height ? props.height : props?.rowData && props?.rowData?.length > 15 ? 800 : 500,
      }}>
      <AgGridReact
        {...props}
        ref={Ref}
        rowData={props.rowData}
        columnDefs={props.colDefs}
        defaultColDef={props.defaultColDef}
        localeText={localeText}
        // aggFuncs={aggFuncs}
        // autoGroupColumnDef={autoGroupColumnDef}
        pagination={true}
        enableRangeSelection={false}
        onFilterChanged={onFilterChanged} // 이미지 엑셀 처리를 위한 현재 노출된 row
        enableCharts={true}
        groupIncludeFooter={props.groupIncludeFooter ?? false}
        groupIncludeTotalFooter={props.groupIncludeTotalFooter ?? false}
        overlayLoadingTemplate={
          '<div aria-live="polite" aria-atomic="true" style="position:absolute;top:0;left:0;right:0; bottom:0; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center no-repeat" aria-label="loading"></div>'
        }
        overlayNoRowsTemplate={
          '<span aria-live="polite" aria-atomic="true" style="padding: 10px;">데이터가 없습니다</span>'
        }
        tooltipShowDelay={props.tooltipShowDelay}
        tooltipHideDelay={props.tooltipHideDelay}
        rowHeight={props.rowHeight}
        autoSizeStrategy={{ type: 'fitCellContents' }}
        pinnedBottomRowData={props.pinnedBottomRowData}
        excelStyles={excelStyles}
        getRowStyle={getRowStyle}
        onRowClicked={props.onClickRow}
      />
    </div>
  );
}

export default AgGrid;

function roundedAvgAggFunction(params: IAggFuncParams) {
  const result = avgAggFunction(params);
  if (result.avg) {
    result.avg = Number(result.avg.toFixed(0));
  }
  return result;
}

function avgAggFunction(params: IAggFuncParams) {
  // the average will be the sum / count
  let sum = 0;
  let count = 0;
  params.values.forEach((value) => {
    const groupNode = value !== null && value !== undefined && typeof value === 'object';
    if (groupNode) {
      // we are aggregating groups, so we take the
      // aggregated values to calculated a weighted average
      sum += value.avg * value.count;
      count += value.count;
    } else {
      // skip values that are not numbers (ie skip empty values)
      if (typeof value === 'number') {
        sum += value;
        count++;
      }
    }
  });
  // avoid divide by zero error
  let avg = null;
  if (count !== 0) {
    avg = sum / count;
  }
  // the result will be an object. when this cell is rendered, only the avg is shown.
  // however when this cell is part of another aggregation, the count is also needed
  // to create a weighted average for the next level.
  const result = {
    count: count,
    avg: avg,
    // the grid by default uses toString to render values for an object, so this
    // is a trick to get the default cellRenderer to display the avg value
    toString: function () {
      return `${this.avg}`;
    },
  };
  return result;
}
